import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Camera, Check, X, Wrench, AlertTriangle, Upload, Trash2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { MaintenanceSettings } from "@/types/checklist";

export type MaintenanceResponseValue = 'yes' | 'no' | 'needs_repair' | null;

interface MaintenanceTaskUIProps {
  taskIndex: number;
  taskText: string;
  roomName: string;
  settings: MaintenanceSettings;
  readOnly?: boolean;
  response: MaintenanceResponseValue;
  notes: string;
  photos: { id: string; file: File; previewUrl: string }[];
  onResponseChange: (response: MaintenanceResponseValue) => void;
  onNotesChange: (notes: string) => void;
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (id: string) => void;
}

const MaintenanceTaskUI = ({
  taskIndex,
  taskText,
  roomName,
  settings,
  readOnly = false,
  response,
  notes,
  photos,
  onResponseChange,
  onNotesChange,
  onAddPhotos,
  onRemovePhoto,
}: MaintenanceTaskUIProps) => {
  const { tDynamic, isRtl } = useTranslation();
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAddPhotos(Array.from(files));
    }
    e.target.value = "";
  };

  const getResponseStyle = (value: MaintenanceResponseValue) => {
    if (response === value) {
      switch (value) {
        case 'yes':
          return 'bg-green-500 text-white ring-2 ring-green-500 ring-offset-2';
        case 'no':
          return 'bg-red-500 text-white ring-2 ring-red-500 ring-offset-2';
        case 'needs_repair':
          return 'bg-amber-500 text-white ring-2 ring-amber-500 ring-offset-2';
        default:
          return '';
      }
    }
    return 'bg-muted hover:bg-muted/80 text-foreground';
  };

  const isComplete = response !== null && 
    (!settings.require_photos || photos.length > 0);

  return (
    <div className="space-y-4 pb-4 border-b last:border-b-0" dir={isRtl ? "rtl" : "ltr"}>
      {/* Task header */}
      <div className="flex items-start gap-3 py-2">
        <span className="text-sm font-medium text-muted-foreground min-w-[24px] pt-0.5 shrink-0">
          {taskIndex + 1}.
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="secondary" className="gap-1 text-xs bg-amber-100 text-amber-700 border-amber-200">
              <Wrench className="w-3 h-3" />
              {tDynamic("Maintenance")}
            </Badge>
            {isComplete && (
              <Badge variant="secondary" className="gap-1 text-xs bg-green-100 text-green-700 border-green-200">
                <Check className="w-3 h-3" />
                {tDynamic("Complete")}
              </Badge>
            )}
          </div>
          <span className="text-base font-medium leading-snug block">{taskText}</span>
        </div>
      </div>

      {/* Yes/No/Needs Repair selector - uses the question field from settings */}
      <div className="space-y-3">
        <Label className="text-sm text-muted-foreground">
          {settings.question ? tDynamic(settings.question) : tDynamic("Is this item working properly?")}
        </Label>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
          <button
            type="button"
            onClick={() => !readOnly && onResponseChange('yes')}
            disabled={readOnly}
            className={`flex items-center justify-center gap-2 py-4 px-6 rounded-full font-semibold text-base transition-all min-h-[56px] flex-1 shadow-sm active:scale-95 ${getResponseStyle('yes')}`}
          >
            <Check className="w-5 h-5" />
            <span>{tDynamic("Yes")}</span>
          </button>
          <button
            type="button"
            onClick={() => !readOnly && onResponseChange('no')}
            disabled={readOnly}
            className={`flex items-center justify-center gap-2 py-4 px-6 rounded-full font-semibold text-base transition-all min-h-[56px] flex-1 shadow-sm active:scale-95 ${getResponseStyle('no')}`}
          >
            <X className="w-5 h-5" />
            <span>{tDynamic("No")}</span>
          </button>
          <button
            type="button"
            onClick={() => !readOnly && onResponseChange('needs_repair')}
            disabled={readOnly}
            className={`flex items-center justify-center gap-2 py-4 px-6 rounded-full font-semibold text-base transition-all min-h-[56px] flex-1 shadow-sm active:scale-95 ${getResponseStyle('needs_repair')}`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="whitespace-nowrap">{tDynamic("Needs Repair")}</span>
          </button>
        </div>
      </div>

      {/* Photos section */}
      {(settings.require_photos || photos.length > 0) && (
        <div className="space-y-3">
          <Label className="text-sm text-muted-foreground flex items-center gap-2">
            <Camera className="w-4 h-4" />
            {settings.require_photos ? tDynamic("Photos (required)") : tDynamic("Add Photos (optional)")}
          </Label>
          
          {/* Photo previews */}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.previewUrl}
                    alt="Maintenance photo"
                    className="w-20 h-20 sm:w-16 sm:h-16 object-cover rounded-lg border"
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(photo.id)}
                      className="absolute -top-1 -right-1 w-5 h-5 sm:w-4 sm:h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow touch-manipulation"
                    >
                      <Trash2 className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Upload button - larger on mobile */}
          {!readOnly && (
            <label className="flex items-center justify-center gap-2 w-full h-14 sm:h-12 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors active:bg-muted/50">
              <Upload className="w-5 h-5 sm:w-4 sm:h-4 text-muted-foreground" />
              <span className="text-base sm:text-sm text-muted-foreground font-medium">
                {tDynamic("Upload photo")}
              </span>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={readOnly}
              />
            </label>
          )}
        </div>
      )}

      {/* Notes section */}
      {settings.allow_notes !== false && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            {tDynamic("Notes (optional)")}
          </Label>
          <Textarea
            placeholder={tDynamic("Add any notes about this item...")}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={3}
            className="text-base resize-none min-h-[80px]"
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};

export default MaintenanceTaskUI;
